import { type AnalyticsBrowser } from '@segment/analytics-next'
import {
  datadogRum,
  type RumEvent,
  type RumResourceEvent,
  type RumFetchResourceEventDomainContext,
  type RumEventDomainContext,
  type RumErrorEvent,
} from '@datadog/browser-rum'
import { matchPath } from 'react-router'
import { isCypressWindow } from '@retailer-platform/shared-common'
import clientEnv from '../global/clientEnv'
import { isDebugMode } from '../core/factories/events/eventDebugging'
import { type GlobalAnalytics } from '../core/factories/events/segmentAnalytics.types'
import { errors } from '../error-handling/errors'
import { initRiseClient } from '../rise/riseClient'
import type { Instacart_Infra_Totem_V1_System as TotemSystem } from '../../__codegen__/api'
import {
  IGNORED_ERRORS,
  IGNORED_REGEX_ERRORS,
  IGNORED_STACK_REGEX_ERRORS,
  IGNORED_UNHANDLED_ERRORS,
} from './ignoredErrors'

// Flag to prevent race condition during async Segment load
let isSegmentInitializing = false

// Track whether initAnalytics has been called with explicit consent
// This helps distinguish "waiting for consent" from "initialization failed"
let analyticsInitAttempted = false

/**
 * True once `initAnalytics` has decided to load Segment — i.e. it ran past every
 * consent gate and reached the load call. This is the earliest point at which the
 * SDK is "expected to be available", so it is the correct gate for the missing-module
 * warning: before this, `global.analytics` is legitimately unset because init has not
 * started, not because the script failed.
 *
 * Do NOT key this off `!PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID`: that reports "enabled"
 * before init even runs, which — once the SDK moved to a dynamic `import()` with a
 * real network hop — produced false "script missing" errors for tracked actions
 * fired during the pre-init/chunk-load window.
 *
 * Returns false while waiting for consent/region detection (OneTrust) or before init
 * has begun (no OneTrust).
 */
export const isAnalyticsEnabled = (): boolean => analyticsInitAttempted

/**
 * True while the Segment SDK chunk is being dynamically imported and loaded.
 * Callers use this to tell "still loading" apart from "attempted but failed":
 * during this window `global.analytics` is legitimately unset, so consumers
 * must not report Segment as missing.
 */
export const isAnalyticsInitializing = (): boolean => isSegmentInitializing

export const initAnalytics = async (enableAnalytics?: boolean) => {
  // If OneTrust is configured and no explicit consent provided, don't initialize
  // (Wait for consent change event to call with explicit true/false)
  if (clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID && enableAnalytics === undefined) {
    console.log('[Segment Analytics] OneTrust configured, waiting for explicit consent')
    return
  }

  // Default to true only if OneTrust is NOT configured (backward compatible)
  const shouldEnableAnalytics = enableAnalytics ?? true

  // Check if Segment is already initialized by checking global analytics object
  const global: GlobalAnalytics = globalThis

  console.log('[Segment Analytics] Checking initialization status:', {
    alreadyInitialized: !!global.analytics,
    isInitializing: isSegmentInitializing,
    enableAnalytics: shouldEnableAnalytics,
    hasOneTrust: !!clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID,
  })

  // Check both global.analytics and the flag to prevent race condition
  if (global.analytics || isSegmentInitializing) {
    console.log('[Segment Analytics] Already initialized or initializing, skipping')
    return
  }

  // If OneTrust is configured and analytics not enabled, don't initialize
  if (clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID && !shouldEnableAnalytics) {
    console.log('[Segment Analytics] Consent not granted, skipping initialization')
    return
  }

  console.log('[Segment Analytics] Initializing with consent:', {
    enableAnalytics: shouldEnableAnalytics,
  })

  // Mark that initialization was explicitly attempted (consent flow completed)
  analyticsInitAttempted = true

  // Set flag immediately to prevent race condition with duplicate calls
  isSegmentInitializing = true

  try {
    Array.from(new Array(localStorage.length))
      .map((_, i) => localStorage.key(i))
      // @ts-expect-error TS18047 — strictNullChecks. TODO: fix strict null check
      .filter(key => key.startsWith('amplitude_unsent'))
      // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
      .forEach(key => localStorage.removeItem(key))
  } catch (err) {
    errors.captureException(err as Error, 'failed to clear amplitude_unsent events')
  }

  if (clientEnv.PUBLIC_CLIENT_SEGMENT_TOKEN) {
    let apiHost = 'analytics.dashboard-api.instacart.com/v1'
    let writeKey = clientEnv.PUBLIC_CLIENT_SEGMENT_TOKEN

    // Check both are set to avoid race conditions on load
    if (clientEnv.PUBLIC_CLIENT_MONGOOSE_WRITE_KEY && clientEnv.PUBLIC_CLIENT_SEGMENT_API_HOST) {
      apiHost = `${clientEnv.PUBLIC_CLIENT_SEGMENT_API_HOST}/v2`
      writeKey = clientEnv.PUBLIC_CLIENT_MONGOOSE_WRITE_KEY
    }

    try {
      // Dynamic import keeps the Segment SDK (~32 KB gz) out of the eager boot chunk.
      // Safe to defer: init is already gated behind OneTrust consent, and the SDK
      // buffers/replays events queued before load completes.
      const { AnalyticsBrowser } = await import('@segment/analytics-next')

      const analytics: AnalyticsBrowser = AnalyticsBrowser.load(
        {
          writeKey,
          // we proxy the initial load of segment cdn to get around adblockers blocking the segment url
          // this base url is used to load the *initial* javascript package from Segment that will allow
          // events to be sent (aka the 3rd party packaged code)
          cdnURL: 'https://analyticscdn.dashboard-api.instacart.com',
        },
        {
          integrations: {
            // only load specified integrations
            All: false,
            'Segment.io': {
              // This is the host that events will get *sent* to after the package has loaded that allows us
              // to send events. Write key must match the corresponding event ingestor it's being sent to.
              // eg. analytics.dashboard-api.instacart.com/v1 -> Segment.io provided URL
              // eg. ipp-mgs.instacart.team -> IPP's Mongoose ingestor for staging
              apiHost,
              protocol: 'https',
            },
          },
        }
      )

      const userAgentMiddleWare = function ({ payload, next }: { payload: any; next: any }) {
        // We currently do not support sending userAgentData to segment
        if (payload?.obj?.context?.userAgentData) {
          delete payload.obj.context.userAgentData
        }
        next(payload)
      }

      analytics.addSourceMiddleware(userAgentMiddleWare)

      const global: GlobalAnalytics = globalThis
      global.analytics = analytics

      // Reset the flag after successful initialization
      isSegmentInitializing = false

      analytics.debug(isDebugMode())

      console.log('[Segment Analytics] Initialized successfully')
    } catch (err) {
      // The dynamic import can reject (chunk/network failure) — a mode the old
      // static import could not hit. Reset the flag so a later consent/route event
      // can retry; otherwise global.analytics stays unset and every send noops for
      // the whole session while segmentAnalytics() re-logs the failure on each call.
      isSegmentInitializing = false
      errors.captureException(err as Error, 'failed to lazy-load Segment analytics')
    }
  } else {
    // No Segment token configured, reset flag
    isSegmentInitializing = false
    console.log('[Segment Analytics] No token configured, skipping initialization')
  }

  // Initialize RISE client alongside Segment (same consent gate)
  initRiseClient()
}

const isResourceEvent = (event: RumEvent): event is RumResourceEvent => event.type === 'resource'

/**
 * Appends the graphql operation name when present on resource events
 */
const enrichResourceWithGraphqlOperationName = (
  event: RumEvent,
  context: RumEventDomainContext
) => {
  if (
    isResourceEvent(event) &&
    event.resource.url.startsWith(clientEnv.PUBLIC_CLIENT_RETAILER_PLATFORM_MESH_URL)
  ) {
    const resourceContext = context as RumFetchResourceEventDomainContext
    if (typeof resourceContext.requestInit?.body === 'string') {
      // extract operationName from body string without parsing JSON
      const operationName = resourceContext.requestInit.body.match(/"operationName":"(.*?)"/)?.[1]

      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      event.context.graphqlOperationName = operationName
    }
  }
}

export const TOTEM_ENTITY_KEY = 'totem-entity'
export const TOTEM_SYSTEM_KEY = 'totem-system'
export const PARTNER_ID_KEY = 'partner-id'
export const RETAILER_ID_KEY = 'retailer-id'
export const RETAILER_IDS_KEY = 'retailer-ids'
let totemRouteOwnerMapping: Record<
  string,
  { routePattern: string; hasDevTooling?: boolean; totemSystem?: TotemSystem }
> = {}
let lastUrl: string | undefined
let lastTotemEntity: string | undefined

const getMatchedKey = (event?: RumEvent) => {
  const url = event?.view?.url
  // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
  const pathname = event ? new URL(url).pathname : window.location.pathname

  const matchedKey = Object.keys(totemRouteOwnerMapping).find(key => {
    if (matchPath(pathname, { path: key, exact: true })?.path) {
      return true
    }
  })

  return matchedKey
}

const enrichWithTotemEntity = (event: RumEvent) => {
  const url = event.view?.url

  if (url === lastUrl) {
    // a bit of memoization to avoid re-processing the same url
    // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
    event.context[TOTEM_ENTITY_KEY] = lastTotemEntity
  } else if (url) {
    lastUrl = url
    lastTotemEntity = undefined

    const matchedKey = getMatchedKey(event)

    // @ts-expect-error TS2538 — strictNullChecks. TODO: fix strict null check
    const totemEntity = totemRouteOwnerMapping[matchedKey]
    if (totemEntity) {
      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      event.context[TOTEM_ENTITY_KEY] = totemEntity.routePattern
      lastTotemEntity = totemEntity.routePattern
    }
  }
}

export const updateTotemSystem = (totemSystem: TotemSystem) => {
  const matchedKey = getMatchedKey()
  // @ts-expect-error TS2538 — strictNullChecks. TODO: fix strict null check
  totemRouteOwnerMapping[matchedKey] = {
    // @ts-expect-error TS2538 — strictNullChecks. TODO: fix strict null check
    ...totemRouteOwnerMapping[matchedKey],
    totemSystem,
  }
}

const enrichWithTotemSystem = (event: RumEvent) => {
  // Only enrich with totem system if it is populated in totemRouteOwnerMapping.
  // This avoids processing the entire totemRouteOwnerMapping on every event,
  // until our ContactHeader populates the totem system for that route. It gurantees
  // that the totem system is present for that event's routes.

  // Totem uses snake_case for all of their keys. We can create a future PR to convert
  // DataDog Mesh + FE totem keys to camelCase if we want to.

  const isTotemSystemPresent = Object.values(totemRouteOwnerMapping).some(
    totem => totem.totemSystem
  )

  if (isTotemSystemPresent) {
    const matchedKey = getMatchedKey(event)

    // @ts-expect-error TS2538 — strictNullChecks. TODO: fix strict null check
    const totemSystem = totemRouteOwnerMapping[matchedKey]?.totemSystem
    if (totemSystem) {
      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      event.context[TOTEM_SYSTEM_KEY] = {
        name: totemSystem.name,
        /* eslint-disable @typescript-eslint/naming-convention */
        owner_team: {
          name: totemSystem.owner_team?.name,
        },
        contact_details: {
          oncall_slack_group: {
            handle: totemSystem.contact_details?.oncall_slack_group?.handle,
          },
          opsgenie_responder: {
            name: totemSystem.contact_details?.opsgenie_responder?.name,
          },
          /* eslint-enable @typescript-eslint/naming-convention */
        },
      }
    }
  }
}

export const setTotemRouteOwnerMapping = (
  mapping: Record<
    string,
    { routePattern: string; hasDevTooling?: boolean; totemSystem?: TotemSystem }
  >
) => {
  totemRouteOwnerMapping = mapping
}

// Track if Datadog has been initialized
let datadogInitialized = false

/**
 * Initialize Datadog RUM once on app start
 * Always initializes with trackingConsent: 'not-granted' if OneTrust is configured
 * Use updateDatadogConsent() to update consent dynamically
 *
 * Note: Datadog will set cookies (dd_s) even with 'not-granted', but no data is collected
 */
export const initDataDogRum = () => {
  if (clientEnv.PUBLIC_CLIENT_DATADOG_RUM_ENABLED !== 'true') {
    console.log('[Datadog RUM] Disabled via config')
    return
  }

  // Skip RUM in Cypress - test traffic shouldn't pollute real metrics
  if (isCypressWindow()) {
    console.log('[Datadog RUM] Disabled in Cypress environment')
    return
  }

  if (datadogInitialized) {
    console.log('[Datadog RUM] Already initialized, skipping')
    return
  }

  const sha = clientEnv.PUBLIC_CLIENT_ISC_SHA
  const branch = clientEnv.PUBLIC_CLIENT_ISC_BRANCH
  const gitCommit = process.env.GIT_COMMIT
  const release = `retailer-platform-web-workspace@${gitCommit}`

  // If OneTrust is configured, start with 'not-granted' and update via setTrackingConsent()
  // Otherwise, default to 'granted' for backward compatibility
  const initialConsent = clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID ? 'not-granted' : 'granted'

  console.log('[Datadog RUM] Initializing:', {
    trackingConsent: initialConsent,
    hasOneTrust: !!clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID,
    timestamp: new Date().toISOString(),
  })

  datadogRum.init({
    applicationId: clientEnv.PUBLIC_CLIENT_DATADOG_RUM_APPLICATION_ID as string,
    clientToken: clientEnv.PUBLIC_CLIENT_DATADOG_RUM_CLIENT_TOKEN as string,
    site: 'datadoghq.com',
    service: 'ipp',
    version: release,
    env: clientEnv.PUBLIC_CLIENT_ENVIRONMENT as string,
    sessionSampleRate: parseInt(
      (clientEnv.PUBLIC_CLIENT_DATADOG_RUM_SAMPLE_RATE as string) || '0',
      10
    ),
    sessionReplaySampleRate: parseInt(
      (clientEnv.PUBLIC_CLIENT_DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE as string) || '0',
      0
    ),
    defaultPrivacyLevel: 'allow', // Don't mask any DOM elements by default
    trackResources: true,
    trackUserInteractions: true,
    trackingConsent: initialConsent, // Set initial consent status
    allowedTracingUrls: [
      clientEnv.PUBLIC_CLIENT_RETAILER_PLATFORM_MESH_URL,
      clientEnv.PUBLIC_CLIENT_RPP_URL,
    ],
    beforeSend: (event, context) => {
      if (event.type === 'error') {
        const error = (event as RumErrorEvent).error

        if (IGNORED_ERRORS.some(ignoredError => error.message?.includes(ignoredError))) {
          return false
        }

        if (IGNORED_REGEX_ERRORS.some(regex => regex.test(error.message))) {
          return false
        }

        // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
        if (IGNORED_STACK_REGEX_ERRORS.some(regex => regex.test(error.stack))) {
          return false
        }

        if (
          clientEnv.PUBLIC_CLIENT_IGNORE_UNHANDLED_ERRORS &&
          error.handling === 'unhandled' &&
          IGNORED_UNHANDLED_ERRORS.some(
            ignoredError =>
              (error.message || '') === ignoredError.message &&
              (error.stack || '') === ignoredError.stack
          )
        ) {
          return false
        }
      }

      enrichResourceWithGraphqlOperationName(event, context)
      enrichWithTotemEntity(event)
      enrichWithTotemSystem(event)
      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      event.context.branch = branch
      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      event.context.sha = sha
      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      event.context.release = release
      return true
    },
  })

  datadogRum.setGlobalContextProperty('totem-entity', PLATFORM_TOTEM_ENTITY)

  datadogInitialized = true
  console.log('[Datadog RUM] Initialized successfully')
}

/**
 * Delete Datadog RUM cookies
 * Called when user revokes consent to ensure cookies are removed
 *
 * Dynamically detects all cookies starting with '_dd' or 'dd_' and deletes them
 */
const deleteDatadogCookies = () => {
  // Get all cookies
  const allCookies = document.cookie.split(';').map(cookie => cookie.trim())

  // Find all Datadog cookies (start with '_dd' or 'dd_')
  const datadogCookieNames = allCookies
    .map(cookie => cookie.split('=')[0])
    .filter(name => name.startsWith('_dd') || name.startsWith('dd_'))

  // Delete each Datadog cookie
  datadogCookieNames.forEach(cookieName => {
    // Delete for current path
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`

    // Delete for root domain
    const domain = window.location.hostname
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`
  })

  console.log(
    '[Datadog RUM] Deleted cookies:',
    datadogCookieNames.length > 0 ? datadogCookieNames : 'No Datadog cookies found'
  )
}

/**
 * Delete Segment cookies manually
 * Used when Segment isn't initialized but cookies exist from previous session
 */
const deleteSegmentCookiesManually = () => {
  const allCookies = document.cookie.split(';').map(cookie => cookie.trim())
  const segmentCookieNames = allCookies
    .map(cookie => cookie.split('=')[0])
    .filter(name => name.startsWith('ajs_'))

  if (segmentCookieNames.length === 0) {
    return
  }

  segmentCookieNames.forEach(cookieName => {
    // Delete for current path
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`

    // Delete for root domain
    const domain = window.location.hostname
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`
  })

  console.log(
    '[Segment Analytics] Deleted stale cookies from previous session:',
    segmentCookieNames
  )
}

/**
 * Reset Segment Analytics and clear user identity
 * Called when user revokes consent to clear tracking data
 *
 * Uses Segment's analytics.reset() API if SDK is loaded, otherwise manually deletes cookies
 *
 * - Clear user ID and anonymous ID from memory
 * - Clear user traits
 * - Delete ajs_* cookies
 *
 * Note: If SDK is loaded, it remains in memory and may still collect events,
 * but without user identity/cookies. Page refresh recommended for full cleanup.
 */
const resetSegmentAnalytics = () => {
  const global: GlobalAnalytics = globalThis

  if (!global.analytics) {
    // Segment not initialized, but may have stale cookies from previous session
    console.log('[Segment Analytics] Not initialized, checking for stale cookies...')
    deleteSegmentCookiesManually()
    return
  }

  try {
    // Use Segment's built-in reset() method
    // This clears user ID, anonymous ID, and traits
    global.analytics.reset()

    console.log('[Segment Analytics] Reset complete: User identity and traits cleared')
    console.warn(
      '[Segment Analytics] SDK remains loaded. Page refresh recommended to fully stop tracking.'
    )
  } catch (error) {
    console.error('[Segment Analytics] Error during reset:', error)

    // Fallback: manually delete cookies if reset() fails
    deleteSegmentCookiesManually()
  }
}

/**
 * Update Datadog RUM tracking consent dynamically
 * This should be called when consent status changes (e.g., via OneTrust)
 * If consent is revoked, also deletes Datadog and Segment cookies
 */
export const updateDatadogConsent = (hasConsent: boolean) => {
  if (clientEnv.PUBLIC_CLIENT_DATADOG_RUM_ENABLED !== 'true') {
    return
  }

  const consentStatus = hasConsent ? 'granted' : 'not-granted'
  console.log('[Datadog RUM] Updating consent:', {
    hasConsent,
    trackingConsent: consentStatus,
    datadogInitialized,
    timestamp: new Date().toISOString(),
  })

  // Update consent if Datadog is already initialized
  if (datadogInitialized) {
    datadogRum.setTrackingConsent(consentStatus)
  } else {
    console.log('[Datadog RUM] Not initialized yet - will initialize with consent:', consentStatus)
  }

  // If consent is revoked, clear tracking data (even if not initialized - clean up stale cookies)
  if (!hasConsent) {
    deleteDatadogCookies()
    resetSegmentAnalytics() // Uses Segment's reset() API
  }
}

export const PLATFORM_TOTEM_ENTITY = 'ipp-platform'
